import React from 'react';
import type { ReactNode } from 'react';
import { ChevronDownIcon, PlayIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import type { AccrualTier, TierFrequency } from '../../types/hr';
import { MONTHS, ordinal, sortedTiers } from '../../utils/policy';
interface AccrualScheduleBuilderProps {
  tiers: AccrualTier[];
  waitingDays: string;
  onChange: (tiers: AccrualTier[]) => void;
  onWaitingChange: (value: string) => void;
}
const DAYS = Array.from({
  length: 28
}, (_, i) => String(i + 1));
export function newTier(afterYears: string, from?: AccrualTier): AccrualTier {
  return {
    id: `tier-${Date.now()}-${Math.round(Math.random() * 1000)}`,
    afterYears,
    days: '',
    frequency: from?.frequency ?? 'Yearly',
    postDay: from?.postDay ?? '1',
    postMonth: from?.postMonth ?? 'January',
    capEnabled: from?.capEnabled ?? false,
    cap: from?.cap ?? '',
    carryoverEnabled: from?.carryoverEnabled ?? true,
    carryover: from?.carryover ?? '5'
  };
}
export function AccrualScheduleBuilder({
  tiers,
  waitingDays,
  onChange,
  onWaitingChange
}: AccrualScheduleBuilderProps) {
  const ordered = sortedTiers(tiers);
  const hasWait = (Number(waitingDays) || 0) > 0 || waitingDays === '';
  const update = (id: string, patch: Partial<AccrualTier>) => onChange(tiers.map(t => t.id === id ? {
    ...t,
    ...patch
  } : t));
  const remove = (id: string) => onChange(tiers.filter(t => t.id !== id));
  const add = () => {
    const last = ordered[ordered.length - 1];
    const nextYears = String((Number(last?.afterYears) || 0) + (ordered.length === 1 ? 1 : 3));
    onChange([...tiers, newTier(nextYears, last)]);
  };
  return <div className="relative" data-id="element-859" data-mp="3lfk9vhb1tqbm">
      <ol className="space-y-4" data-id="element-860" data-mp="3qcapy4muslx1">
        {ordered.map((t, i) => {
        const isStart = i === 0;
        return <li key={t.id} className="relative pl-10" data-id="element-861" data-mp="29x9fcqawmbsl" data-mpi={i}>
              {i < ordered.length - 1 && <span className="absolute bottom-[-16px] left-[13px] top-8 w-px bg-slate-200" aria-hidden="true" data-id="element-862" data-mp="2exx578necml8" />}
              <span className={`absolute left-0 top-0.5 flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${isStart ? 'bg-flame text-white' : 'bg-navy text-white'}`} data-id="element-863" data-mp="kg43n41n3itl">
                {isStart ? <PlayIcon className="h-3 w-3 fill-current" data-id="element-864" data-mp="aqausasvtht1" /> : i}
              </span>

              <div className="rounded-2xl p-5 ring-1 ring-inset ring-slate-200" data-id="element-865" data-mp="6qk56af3d7q3">
                <div className="flex items-center justify-between gap-3" data-id="element-866" data-mp="1qed4t4wtj7h">
                  <h3 className="text-sm font-semibold text-navy" data-id="element-867" data-mp="1obbfbuea1mx">{isStart ? 'Starting schedule' : `Milestone ${i}`}</h3>
                  {!isStart && <button type="button" onClick={() => remove(t.id)} className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium text-muted transition hover:bg-rose-50 hover:text-rose-600" data-id="element-868" data-mp="3nynmjfyfhcbq">
                      <Trash2Icon className="h-3.5 w-3.5" data-id="element-869" data-mp="1m5zij4dymkuw" /> Remove milestone
                    </button>}
                </div>

                {!isStart && <div className="mt-4 flex flex-wrap items-center gap-2 border-b border-slate-100 pb-4 text-sm text-slate-600" data-id="element-870" data-mp="301hicbhy6e7n">
                    <span data-id="element-871" data-mp="34zj3d1yxonlr">Employees reach this milestone</span>
                    <NumberInput label="Years after hire" value={t.afterYears} onChange={v => update(t.id, {
                afterYears: v
              })} data-id="element-872" data-mp="39tezdyvn00ea" />
                    <span data-id="element-873" data-mp="3dt5tockdgynk">{Number(t.afterYears) === 1 ? 'year' : 'years'} after hire date</span>
                  </div>}

                <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-slate-600" data-id="element-874" data-mp="2m2huv76685d2">
                  <span data-id="element-875" data-mp="2q4moi5wmd1zp">Employees accrue</span>
                  <NumberInput label="Days" value={t.days} onChange={v => update(t.id, {
                days: v
              })} placeholder="TBD" data-id="element-876" data-mp="2q2tf4iumg3o0" />
                  <span data-id="element-877" data-mp="2v1d46s4ihvkl">days</span>
                  <Select label="Frequency" value={t.frequency} options={['Yearly', 'Monthly']} onChange={v => update(t.id, {
                frequency: v as TierFrequency
              })} data-id="element-878" data-mp="5enii1z4x2zq" />
                  <span data-id="element-879" data-mp="afbi3c1fis5k">on the</span>
                  <Select label="Day" value={t.postDay} options={DAYS} format={ordinal} onChange={v => update(t.id, {
                postDay: v
              })} data-id="element-880" data-mp="8vjm56sw93s0" />
                  {t.frequency === 'Yearly' ? <>
                      <span data-id="element-881" data-mp="hwzwjejpl5w8">day of</span>
                      <Select label="Month" value={t.postMonth} options={MONTHS} onChange={v => update(t.id, {
                  postMonth: v
                })} data-id="element-882" data-mp="hy2lhgxh0y9i" />
                    </> : <span data-id="element-883" data-mp="rtmgm147nsx7">day of each month</span>}
                </div>

                {isStart && <Question title="Is there a waiting period before new employees begin accruing time?" data-id="element-884" data-mp="mtqe66b5w9jr">
                    <Radio checked={!hasWait} onChange={() => onWaitingChange('0')} label="No, they begin accruing time immediately on hire" data-id="element-885" data-mp="11mtnxriihizx" />
                    <Radio checked={hasWait} onChange={() => onWaitingChange(hasWait ? waitingDays : '30')} label="Yes, there is a waiting period" data-id="element-886" data-mp="1aknfyq98tqha" />
                    {hasWait && <div className="ml-7 flex items-center gap-2 text-sm text-slate-600" data-id="element-887" data-mp="1p8drozw8yw6z">
                        <NumberInput label="Waiting days" value={waitingDays} onChange={onWaitingChange} data-id="element-888" data-mp="1u6yqqvzrj9tu" />
                        <span data-id="element-889" data-mp="1g7z1bm4feqwm">days after hire date</span>
                      </div>}
                  </Question>}

                <Question title="Is there a cap on the balance an employee can accrue?" data-id="element-890" data-mp="1k77v2qzguos6">
                  <Radio checked={!t.capEnabled} onChange={() => update(t.id, {
                capEnabled: false
              })} label="No, they will continue to accrue time, regardless of balance" data-id="element-891" data-mp="16g1pqe9i9wxd" />
                  <Radio checked={t.capEnabled} onChange={() => update(t.id, {
                capEnabled: true
              })} label="Yes, they stop accruing at a certain balance" data-id="element-892" data-mp="wk8507lmux4i" />
                  {t.capEnabled && <div className="ml-7 flex items-center gap-2 text-sm text-slate-600" data-id="element-893" data-mp="niibmw86xo43">
                      <span data-id="element-894" data-mp="sid1qxk569o3">Stop at</span>
                      <NumberInput label="Cap" value={t.cap} onChange={v => update(t.id, {
                  cap: v
                })} placeholder="TBD" data-id="element-895" data-mp="shbtf0uig1a2" />
                      <span data-id="element-896" data-mp="2u8vgxsg8g1e6">days</span>
                    </div>}
                </Question>

                <Question title="Can unused time off be carried over after the carryover date (Dec 31)?" data-id="element-897" data-mp="2kh7iv8p67m3q">
                  <Radio checked={!t.carryoverEnabled} onChange={() => update(t.id, {
                carryoverEnabled: false
              })} label="No, it's “use it or lose it”" data-id="element-898" data-mp="2gfw1fqgga76m" />
                  <Radio checked={t.carryoverEnabled} onChange={() => update(t.id, {
                carryoverEnabled: true
              })} label="Yes, time can be carried over" data-id="element-899" data-mp="3d5d55zvvhj5o" />
                  {t.carryoverEnabled && <div className="ml-7 flex items-center gap-2 text-sm text-slate-600" data-id="element-900" data-mp="343nc6pmcec5s">
                      <span data-id="element-901" data-mp="2z5c4oljisdqq">Up to</span>
                      <NumberInput label="Carryover days" value={t.carryover} onChange={v => update(t.id, {
                  carryover: v
                })} placeholder="TBD" data-id="element-902" data-mp="2z8gd5v40pvn7" />
                      <span data-id="element-903" data-mp="1npx65dbnxdti">days</span>
                    </div>}
                </Question>
              </div>
            </li>;
      })}
      </ol>

      <button type="button" onClick={add} className="mt-4 inline-flex items-center gap-2 rounded-xl px-2 py-1.5 text-sm font-medium text-azure transition hover:bg-azure/10" data-id="element-904" data-mp="87hfttti6nfi">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-azure text-white" data-id="element-905" data-mp="c7rjv0xqj3rq">
          <PlusIcon className="h-3.5 w-3.5" data-id="element-906" data-mp="lze4tozvkh7o" />
        </span>
        Add another milestone
      </button>
    </div>;
}
function Question({
  title,
  children
}: {
  title: string;
  children: ReactNode;
}) {
  return <fieldset className="mt-5 border-t border-slate-100 pt-4" data-id="element-907" data-mp="vxv02y5ujhzj">
      <legend className="sr-only" data-id="element-908" data-mp="vyd6gnm83cl6">{title}</legend>
      <p className="text-xs font-medium text-slate-600" aria-hidden="true" data-id="element-909" data-mp="zzgj718z4si6">
        {title}
      </p>
      <div className="mt-2.5 space-y-2" data-id="element-910" data-mp="2xpq7h1l2l0by">{children}</div>
    </fieldset>;
}
function Radio({
  checked,
  onChange,
  label
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return <label className="flex cursor-pointer items-center gap-3 text-sm" data-id="element-911" data-mp="2itrtrt0v5yre">
      <input type="radio" checked={checked} onChange={onChange} className="sr-only" data-id="element-912" data-mp="2dv84bin2a4ua" />
      <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full ring-1 ring-inset transition ${checked ? 'bg-azure ring-azure' : 'bg-white ring-slate-300'}`} aria-hidden="true" data-id="element-913" data-mp="2dt3z8xdfezsf">
        {checked && <span className="h-1.5 w-1.5 rounded-full bg-white" data-id="element-914" data-mp="1zvfia0mhccsn" />}
      </span>
      <span className={checked ? 'font-medium text-navy' : 'text-slate-600'} data-id="element-915" data-mp="1zxjia7h9crnn">{label}</span>
    </label>;
}
function NumberInput({
  label,
  value,
  onChange,
  placeholder
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return <input aria-label={label} inputMode="decimal" value={value} placeholder={placeholder} onChange={e => onChange(e.target.value.replace(/[^0-9.]/g, ''))} className="h-10 w-16 rounded-xl border-0 bg-white px-3 text-sm font-medium text-navy ring-1 ring-inset ring-slate-200 placeholder:text-amber-500 focus:outline-none focus:ring-2 focus:ring-azure" data-id="element-916" data-mp="23w950qo6rljy" />;
}
function Select({
  label,
  value,
  options,
  onChange,
  format
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
  format?: (v: string) => string;
}) {
  return <span className="relative inline-flex" data-id="element-917" data-mp="3iv6b8tdhijmx">
      <select aria-label={label} value={value} onChange={e => onChange(e.target.value)} className="h-10 cursor-pointer appearance-none rounded-xl border-0 bg-white pl-3 pr-8 text-sm font-medium text-navy ring-1 ring-inset ring-slate-200 focus:outline-none focus:ring-2 focus:ring-azure" data-id="element-918" data-mp="3ivy807itines">
        {options.map(o => <option key={o} value={o} data-id="element-919" data-mp="5maa4vwct91t">
            {format ? format(o) : o}
          </option>)}
      </select>
      <ChevronDownIcon className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true" data-id="element-920" data-mp="tbuu9i48ucco" />
    </span>;
}